import { CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import { GroupManagerService } from '../../group-manager/gm_service'
import UserManagerKeys from '../um_keys'
import { UserRow } from '../um_scopes'
import { UserManagerService } from '../um_service'
import { UserChangePasswordFormKeys } from './change-password'
import { UserManagerFormKeys } from './um_form_keys'
import UserManagerFormScope from './um_form_scope'
import { generateRandomSequence } from 'src/utils/string.utils'
import { AppStorage } from 'src/utils'

const LOG = Logger.get('UserManagerFormPresenter')
const userManagerService = UserManagerService.singleton()
const groupManagerService = GroupManagerService.singleton()
const appStorage = AppStorage.singleton()

export class UserManagerFormPresenter extends CubePresenter<MainPresenter, UserManagerFormScope> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new UserManagerFormScope())
    }

    // :: Instance
    #userId?: number

    // :: Methods

    buscarUsuarioSelecionado = async (userId?: number) => {
        if (!userId) return

        try {
            this.scope.waiting = new WaitingScope()
            LOG.debug(`buscarUsuarioSelecionado ${userId}`)
            const user = await userManagerService.fetchUserById(userId)
            if (user) {
                this.scope.onFormDataChange(user)
                this.scope.userEditing = user
                this.scope.protectedGestor = !!user.admin_gestao && !appStorage.adminGestaoEnabled
                this.#userId = userId
            }
        } finally {
            this.scope.waiting = undefined
        }
    }

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new UserManagerFormKeys(this.app, intent)

        keys.dialogSlot(this.scope)

        LOG.debug(`applyParameters ${keys.userId}`)
        if (initialization) {
            await this.#initialize()
            await this.#loadGrupos()
        }

        if (keys.userId && this.#userId !== keys.userId) {
            await this.buscarUsuarioSelecionado(keys.userId)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new UserManagerFormKeys(this.app, intent)
        keys.userId = this.#userId
        LOG.debug(`publishParameters ${keys.userId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.action(this.handleClose)
        this.scope.onSave = this.action(this.handleSave)
        this.scope.onChangePassword = this.action(this.handleChangePassword)
        this.scope.onFormDataChange = this.action(this.handleFormDataChange)
        this.scope.onGenerateRandomPassword = this.action(this.handleGenerateRandomPassword)
        this.scope.adminGestaoEditable = appStorage.adminGestaoEnabled
    }

    private async handleClose() {
        await new UserManagerKeys(this.app).flip()
    }

    private async handleChangePassword() {
        if (this.scope.protectedGestor) {
            this.app.notify('warning', 'Somente um gestor de acessos pode alterar a senha de outro gestor')
            return
        }
        await new UserChangePasswordFormKeys(this.app).flip()
    }

    private async handleSave() {
        try {
            if (!this.scope.formData.name) {
                this.app.notify('warning', 'Usuário deve ser informado')
                return
            }
            if (!this.scope.formData.password && this.scope.formData.id === undefined) {
                this.app.notify('warning', 'Senha deve ser informada')
                return
            }

            const retSalvar = this.scope.formData.id
                ? await userManagerService.updateUser(this.scope.formData.id, this.scope.formData)
                : await userManagerService.createUser(this.scope.formData)

            if (retSalvar) {
                const target = new UserManagerKeys(this.app)

                await target.flip()
                this.app.notify('success', 'Salvo com sucesso')
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }

    async #loadGrupos() {
        LOG.debug('loadGrupos')
        const grupos = await groupManagerService.fetchGroups()
        this.scope.groups = grupos
    }

    private async handleGenerateRandomPassword() {
        this.scope.onFormDataChange({ password: generateRandomSequence() })
    }

    private async handleFormDataChange(dadosAlterados: Partial<UserRow>) {
        this.scope.formData = {
            ...this.scope.formData,
            ...dadosAlterados
        }
    }
}

export default UserManagerFormPresenter
